import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../utils/api';
import { usePortal } from '../../../context/PortalContext';
import { useToast } from '../../../context/ToastContext';
import styles from './StatEntry.module.css';

/**
 * StatEntry Tab
 *
 * Shows all categories at once. Officers enter values for any/all
 * categories on a given date and submit in one action.
 */

// Helper to detect if a category is for mileage
function isMileageCategory(name, abbreviation = '') {
  const normalize = (str) => {
    return String(str).toLowerCase().replace(/[^a-z0-9]/g, '');
  };
  const nameNorm = normalize(name);
  const abbrNorm = normalize(abbreviation);
  return (
    nameNorm.includes('miles') ||
    nameNorm.includes('mileage') ||
    nameNorm.includes('odometer') ||
    abbrNorm === 'mp' ||
    abbrNorm === 'miles'
  );
}

// Number inputs change their value when the mouse wheel is used while focused.
// Release focus first so the wheel continues to scroll the page instead.
function handleNumberInputWheel(event) {
  event.currentTarget.blur();
}

function StatEntry() {
  const { state: portalState } = usePortal();
  const { pushToast } = useToast();
  const api = useApi();

  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [values, setValues] = useState({});
  const [mileageValues, setMileageValues] = useState({});
  const [notes, setNotes] = useState('');
  const [existingValues, setExistingValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [draftReady, setDraftReady] = useState(false);

  const categories = portalState.categories || [];

  const hasCategories = categories.length > 0;
  const hasLockedValues = useMemo(
    () => Object.keys(existingValues).length > 0,
    [existingValues]
  );

  useEffect(() => {
    if (!selectedDate || !hasCategories) {
      setExistingValues({});
      setValues({});
      setMileageValues({});
      setNotes('');
      return;
    }

    let mounted = true;
    const loadExistingForDate = async () => {
      setLoadingExisting(true);
      try {
        const data = await api.getMyStatsForDate(selectedDate);
        const stats = data?.stats || {};
        if (!mounted) return;
        let localDraft = {};
        try { localDraft = JSON.parse(localStorage.getItem(`dst_stat_draft_${selectedDate}`) || '{}'); } catch (e) {}
        const serverDraft = data?.draft || {};
        const draft = Object.keys(serverDraft).length ? serverDraft : localDraft;
        setExistingValues(stats);
        setValues({ ...(draft.values || {}), ...stats });
        setMileageValues({ ...(draft.mileage || {}), ...(data?.mileage || {}) });
        setNotes(draft.notes || '');
        
        // Load mileage values from localStorage if available
        const storageKey = `dst_mileage_${selectedDate}`;
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored && !Object.keys(stats).length && !Object.keys(draft.mileage || {}).length) {
            const parsed = JSON.parse(stored);
            setMileageValues(parsed);
          }
        } catch (e) {}
      } catch (err) {
        if (!mounted) return;
        setExistingValues({});
        setMileageValues({});
        pushToast(err.message || 'Failed to load stats for selected date.', 'error');
      } finally {
        if (mounted) {
          setLoadingExisting(false);
          setDraftReady(true);
        }
      }
    };

    loadExistingForDate();
    return () => {
      mounted = false;
    };
  }, [selectedDate, hasCategories]);

  useEffect(() => {
    if (!draftReady || loadingExisting || !selectedDate) return undefined;
    const timer = setTimeout(() => {
      const unlockedValues = {};
      const unlockedMileage = {};
      Object.entries(values).forEach(([id, value]) => {
        if (!Object.prototype.hasOwnProperty.call(existingValues, String(id)) && value !== '') unlockedValues[id] = value;
      });
      Object.entries(mileageValues).forEach(([id, value]) => {
        if (!Object.prototype.hasOwnProperty.call(existingValues, String(id)) && value && (value.begin !== '' || value.end !== '')) unlockedMileage[id] = value;
      });
      api.saveStatDraft(selectedDate, { values: unlockedValues, mileage: unlockedMileage, notes }).catch(() => {});
    }, 750);
    return () => clearTimeout(timer);
  }, [values, mileageValues, notes, selectedDate, existingValues, draftReady, loadingExisting]);

  const handleValueChange = (categoryId, value) => {
    setValues((prev) => {
      const result = { ...prev, [categoryId]: value };
      persistDraft({ values: result });
      return result;
    });
  };

  const persistDraft = (updates) => {
    try {
      const key = `dst_stat_draft_${selectedDate}`;
      const current = JSON.parse(localStorage.getItem(key) || '{}');
      localStorage.setItem(key, JSON.stringify({ ...current, ...updates }));
    } catch (e) {}
  };

  const handleMileageChange = (categoryId, field, value) => {
    setMileageValues((prev) => {
      const current = prev[categoryId] || { begin: '', end: '', total: '' };
      const updated = { ...current, [field]: value };
      
      // Auto-calculate total
      if (field === 'begin' || field === 'end') {
        const begin = parseInt(updated.begin, 10);
        const end = parseInt(updated.end, 10);
        if (!isNaN(begin) && !isNaN(end) && end >= begin) {
          updated.total = String(end - begin);
        } else {
          updated.total = '';
        }
      }
      
      const result = { ...prev, [categoryId]: updated };
      
      // Persist to localStorage
      const storageKey = `dst_mileage_${selectedDate}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify(result));
      } catch (e) {}
      persistDraft({ mileage: result });
      
      return result;
    });
  };

  const selectRelativeDate = (dayOffset) => {
    setDraftReady(false);
    setSelectedDate(getRelativeDateString(dayOffset));
  };

  const requestChange = async (category, valueField = 'stat_value') => {
    const mileage = mileageValues[String(category.id)] || mileageValues[category.id] || {};
    const currentValue = valueField === 'mileage_begin'
      ? mileage.begin
      : valueField === 'mileage_end'
        ? mileage.end
        : (existingValues[String(category.id)] ?? existingValues[category.id] ?? '');
    const fieldLabel = valueField === 'mileage_begin' ? 'beginning mileage' : valueField === 'mileage_end' ? 'ending mileage' : category.name;
    const nextValue = window.prompt(`Request change for ${fieldLabel}. Enter new value:`, String(currentValue ?? ''));
    if (nextValue === null) return;
    if (nextValue.trim() === '') {
      pushToast('Change request value is required.', 'error');
      return;
    }

    const reason = window.prompt('Reason for change (optional):', '') || '';

    setLoading(true);
    try {
      await api.submitChangeRequest(category.id, selectedDate, nextValue.trim(), reason, valueField);
      pushToast(`${fieldLabel}: change request submitted.`, 'success');
    } catch (err) {
      pushToast(err.message || 'Failed to submit change request.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toSubmit = categories.filter((cat) => {
      const v = values[cat.id];
      const locked = Object.prototype.hasOwnProperty.call(existingValues, String(cat.id));
      if (locked) return false;
      
      // For mileage categories, check if we have begin/end values
      if (isMileageCategory(cat.name, cat.abbreviation)) {
        const m = mileageValues[cat.id];
        return m && m.begin !== '' && m.end !== '';
      }
      
      return v !== undefined && v !== '';
    });

    if (toSubmit.length === 0) {
      pushToast('No new stats to submit for this date.', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {};
      toSubmit.forEach((cat) => {
        if (isMileageCategory(cat.name, cat.abbreviation)) {
          const m = mileageValues[cat.id];
          const begin = parseInt(m.begin, 10);
          const end = parseInt(m.end, 10);
          if (!isNaN(begin) && !isNaN(end) && end >= begin) {
            payload[String(cat.id)] = { begin, end };
          }
        } else {
          payload[String(cat.id)] = values[cat.id];
        }
      });

      await api.submitDailyStats(selectedDate, payload);

      const refreshed = await api.getMyStatsForDate(selectedDate);
      const refreshedStats = refreshed?.stats || {};
      setExistingValues(refreshedStats);
      setValues((prev) => ({ ...prev, ...refreshedStats }));
      setMileageValues(refreshed?.mileage || {});
      
      // Clear localStorage for this date
      const storageKey = `dst_mileage_${selectedDate}`;
      try {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`dst_stat_draft_${selectedDate}`);
      } catch (e) {}

      const count = toSubmit.length;
      pushToast(
        count + ' stat' + (count !== 1 ? 's' : '') + ' submitted successfully.',
        'success'
      );
      setNotes('');
    } catch (err) {
      pushToast(err.message || 'Failed to submit stats.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Enter Stats</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="stat-date" className={styles.label}>
            Date
          </label>
          <input
            id="stat-date"
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setDraftReady(false);
              setSelectedDate(e.target.value);
            }}
            className={styles.input}
            disabled={loading}
          />
          <div className={styles.dateShortcuts} aria-label="Quick date selection">
            {[
              { label: 'Yesterday', offset: -1 },
              { label: 'Today', offset: 0 },
              { label: 'Tomorrow', offset: 1 },
            ].map(({ label, offset }) => {
              const date = getRelativeDateString(offset);
              const isSelected = selectedDate === date;
              return (
                <button
                  key={label}
                  type="button"
                  className={`${styles.dateShortcutButton} ${isSelected ? styles.dateShortcutButtonActive : ''}`.trim()}
                  onClick={() => selectRelativeDate(offset)}
                  disabled={loading}
                  aria-pressed={isSelected}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {categories.length === 0 ? (
          <p className={styles.empty}>No categories configured yet.</p>
        ) : (
          <div className={styles.categoryGrid}>
            {categories.map((cat) => {
              const isLocked = Object.prototype.hasOwnProperty.call(existingValues, String(cat.id));
              const isMileage = isMileageCategory(cat.name, cat.abbreviation);
              
              return (
                <div
                  key={cat.id}
                  className={`${styles.categoryItem} ${isMileage && !isLocked ? styles.mileageCategoryItem : ''}`.trim()}
                >
                  <label className={styles.categoryLabel}>
                    {cat.name}
                    {cat.abbreviation && (
                      <span className={styles.abbr}> ({cat.abbreviation})</span>
                    )}
                  </label>
                  {isLocked && (
                    <span className={styles.lockedBadge}>Already submitted</span>
                  )}
                  
                  {isMileage ? (
                    <div className={styles.mileageGrid}>
                      <div className={styles.mileageField}>
                        <label className={styles.mileageLabel}>Begin</label>
                        <input
                          type="number"
                          min="0"
                          value={mileageValues[cat.id]?.begin ?? ''}
                          onChange={(e) => handleMileageChange(cat.id, 'begin', e.target.value)}
                          onWheel={handleNumberInputWheel}
                          className={styles.mileageInput}
                          disabled={loading || loadingExisting || isLocked}
                          placeholder="Start"
                        />
                      </div>
                      <div className={styles.mileageField}>
                        <label className={styles.mileageLabel}>End</label>
                        <input
                          type="number"
                          min="0"
                          value={mileageValues[cat.id]?.end ?? ''}
                          onChange={(e) => handleMileageChange(cat.id, 'end', e.target.value)}
                          onWheel={handleNumberInputWheel}
                          className={styles.mileageInput}
                          disabled={loading || loadingExisting || isLocked}
                          placeholder="End"
                        />
                      </div>
                      <div className={styles.mileageField}>
                        <label className={styles.mileageLabel}>Total</label>
                        <input
                          type="number"
                          value={mileageValues[cat.id]?.total ?? ''}
                          onWheel={handleNumberInputWheel}
                          className={styles.mileageInput}
                          disabled
                          placeholder="Auto"
                        />
                      </div>
                      {isLocked && (
                        <>
                          <button type="button" className={styles.requestChangeButton} disabled={loading || loadingExisting || !mileageValues[cat.id] || mileageValues[cat.id].begin === ''} onClick={() => requestChange(cat, 'mileage_begin')}>Change Begin</button>
                          <button type="button" className={styles.requestChangeButton} disabled={loading || loadingExisting || !mileageValues[cat.id] || mileageValues[cat.id].end === ''} onClick={() => requestChange(cat, 'mileage_end')}>Change End</button>
                        </>
                      )}
                    </div>
                  ) : (
                    <input
                      id={`cat-${cat.id}`}
                      type="number"
                      min="0"
                      value={
                        isLocked
                          ? existingValues[String(cat.id)]
                          : (values[cat.id] ?? '')
                      }
                      onChange={(e) => handleValueChange(cat.id, e.target.value)}
                      onWheel={handleNumberInputWheel}
                      className={styles.categoryInput}
                      disabled={loading || loadingExisting || isLocked}
                    />
                  )}
                  
                  {isLocked && !isMileage && (
                    <button
                      type="button"
                      className={styles.requestChangeButton}
                      disabled={loading || loadingExisting}
                      onClick={() => requestChange(cat)}
                    >
                      Request Change
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="notes" className={styles.label}>
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              persistDraft({ notes: e.target.value });
            }}
            className={styles.textarea}
            placeholder="Any relevant details..."
            disabled={loading}
            rows="3"
          />
        </div>

        <button
          type="submit"
          className={styles.button}
          disabled={loading || loadingExisting || categories.length === 0}
        >
          {loading ? 'Submitting…' : 'Submit Stats'}
        </button>
        {hasLockedValues && (
          <p className={styles.empty}>Submitted values are locked. Use Request Change for edits.</p>
        )}
      </form>
    </div>
  );
}

function getTodayString() {
  return getRelativeDateString(0);
}

function getRelativeDateString(dayOffset) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + dayOffset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default StatEntry;
